// src/services/sse.js
let _es = null; // EventSource ativo
const _listeners = new Map(); // Map<string, Set<Function>>

function buildSseUrl() {
  const base =
    window.__ENV && window.__ENV.API_BASE
      ? String(window.__ENV.API_BASE)
      : "/api";
  const norm = base.endsWith("/") ? base.slice(0, -1) : base;
  return `${norm}/evento`;
}

function ensureES() {
  if (_es instanceof EventSource) return _es;
  const url = buildSseUrl();
  _es = new EventSource(url);

  _es.onmessage = (ev) => {
    const set = _listeners.get("message");
    if (set && set.size) {
      for (const fn of set) {
        try {
          fn(ev);
        } catch (e) {
          console.error("[SSE:onmessage] handler error:", e);
        }
      }
    }
  };

  _es.onerror = (e) => {
    console.warn("[SSE] erro na conexão SSE, tentando reconectar…", e);
  };

  _es.onopen = () => {
    // conectado
  };

  return _es;
}

/** Sempre retorna a instância viva do EventSource. */
export function getEventSource() {
  return ensureES();
}

/** Core: assinar eventos. Retorna função para desinscrever. */
export function on(eventName = "message", handler) {
  if (typeof handler !== "function") return () => {};
  const name = String(eventName || "message");

  let set = _listeners.get(name);
  if (!set) {
    set = new Set();
    _listeners.set(name, set);
  }
  set.add(handler);

  const es = ensureES();
  if (name !== "message") {
    const wrapped = (ev) => {
      try {
        handler(ev);
      } catch (e) {
        console.error(`[SSE:${name}] handler error:`, e);
      }
    };
    handler.__sse_wrapped = wrapped;
    es.addEventListener(name, wrapped);
  }
  return () => off(name, handler);
}

/** Compat: igual ao on(), mas tenta parsear JSON e entrega payload limpo. */
export function subscribe(eventName = "message", handler) {
  if (typeof handler !== "function") return () => {};
  return on(eventName || "message", (ev) => {
    let payload = ev && ev.data;
    try {
      payload = typeof payload === "string" ? JSON.parse(payload) : payload;
    } catch (e) {
      // Mantém payload original quando não for JSON
      void e; // satisfaz no-empty sem poluir o console
    }
    try {
      handler(payload);
    } catch (e2) {
      console.error("[SSE:subscribe] handler error:", e2);
    }
  });
}

/** Remover assinatura. */
export function off(eventName = "message", handler) {
  const name = String(eventName || "message");
  const set = _listeners.get(name);
  if (!set || !set.has(handler)) return;

  set.delete(handler);
  if (set.size === 0) _listeners.delete(name);

  if (_es instanceof EventSource && name !== "message") {
    const wrapped = handler.__sse_wrapped;
    if (wrapped) {
      try {
        _es.removeEventListener(name, wrapped);
      } catch (e) {
        console.error(`[SSE:off] remove error:`, e);
      }
      delete handler.__sse_wrapped;
    }
  }
}

/** Hook de abertura. Retorna unsubscriber. */
export function onOpen(handler) {
  const es = ensureES();
  if (typeof handler !== "function") return () => {};
  const wrapped = () => {
    try {
      handler();
    } catch (e) {
      console.error("[SSE:onOpen] handler error:", e);
    }
  };
  es.addEventListener("open", wrapped);
  return () => es.removeEventListener("open", wrapped);
}

/** Hook de erro. Retorna unsubscriber. */
export function onError(handler) {
  const es = ensureES();
  if (typeof handler !== "function") return () => {};
  const wrapped = (evt) => {
    try {
      handler(evt);
    } catch (e) {
      console.error("[SSE:onError] handler error:", e);
    }
  };
  es.addEventListener("error", wrapped);
  return () => es.removeEventListener("error", wrapped);
}

/** Fechar tudo. */
export function closeAll() {
  if (_es instanceof EventSource) {
    try {
      _es.close();
    } catch (e) {
      console.error("[SSE:close] error:", e);
    }
  }
  _es = null;
  _listeners.clear();
}
